import type { EndpointDefinition } from '../types'

const parseJsonArray = (rawValue: string): string[] => {
  try {
    const parsed = JSON.parse(rawValue)
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean)
    }
  } catch {
    // fallback to csv parsing below
  }

  return rawValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export const endpointDefinitions: EndpointDefinition[] = [
  {
    id: 'health',
    title: 'Service Health',
    description: 'Ping API health endpoint',
    category: 'Health/Ops',
    method: 'GET',
    pathTemplate: '/health',
    fields: [],
    buildRequest: () => ({ path: '/health' }),
  },
  {
    id: 'opsFlowHealth',
    title: 'Flow Health',
    description: 'Ops health for concrete flow number',
    category: 'Health/Ops',
    method: 'GET',
    pathTemplate: '/ops/flows/:flowNo/health',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '172',
        required: true,
      },
    ],
    buildRequest: (values) => {
      const flowNo = values.flowNo?.trim() || '172'
      return {
        path: `/ops/flows/${encodeURIComponent(flowNo)}/health`,
      }
    },
  },
  {
    id: 'opsDailySummary',
    title: 'HQ Daily Summary',
    description: 'Aggregated summary for HQ flows',
    category: 'Health/Ops',
    method: 'GET',
    pathTemplate: '/ops/hq/daily-summary?flows=:flows',
    fields: [
      {
        key: 'flows',
        label: 'flows (csv)',
        defaultValue: '172,173,174',
      },
    ],
    buildRequest: (values) => ({
      path: '/ops/hq/daily-summary',
      query: {
        flows: values.flows?.trim() || '172,173,174',
      },
    }),
  },
  {
    id: 'makeupsList',
    title: 'Makeups List',
    description: 'List makeups with optional filters',
    category: 'Makeups Queue',
    method: 'GET',
    pathTemplate: '/makeups?flowNo=:flowNo&status=:status&overdue=:overdue',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '172',
      },
      {
        key: 'status',
        label: 'status',
        placeholder: 'open / offered / booked ...',
      },
      {
        key: 'overdue',
        label: 'overdue',
        placeholder: 'true / false',
      },
    ],
    buildRequest: (values) => ({
      path: '/makeups',
      query: {
        flowNo: values.flowNo?.trim() || undefined,
        status: values.status?.trim() || undefined,
        overdue: values.overdue?.trim() || undefined,
      },
    }),
  },
  {
    id: 'makeupsOverdue',
    title: 'Makeups Overdue',
    description: 'Fetch overdue makeups queue',
    category: 'Makeups Queue',
    method: 'GET',
    pathTemplate: '/makeups/overdue?flowNo=:flowNo',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '172',
      },
    ],
    buildRequest: (values) => ({
      path: '/makeups/overdue',
      query: {
        flowNo: values.flowNo?.trim() || undefined,
      },
    }),
  },
  {
    id: 'makeupNotifications',
    title: 'Makeup Notifications',
    description: 'Get notification payload for a makeup request',
    category: 'Makeups Queue',
    method: 'GET',
    pathTemplate: '/makeups/:id/notifications',
    fields: [
      {
        key: 'id',
        label: 'makeup id',
        required: true,
      },
    ],
    buildRequest: (values) => ({
      path: `/makeups/${encodeURIComponent(values.id?.trim() || '')}/notifications`,
    }),
  },
  {
    id: 'makeupPatchStatus',
    title: 'Patch Makeup Status',
    description: 'Change status for one makeup item',
    category: 'Makeups Actions',
    method: 'PATCH',
    pathTemplate: '/makeups/:id/status',
    fields: [
      {
        key: 'id',
        label: 'makeup id',
        required: true,
      },
      {
        key: 'status',
        label: 'new status',
        defaultValue: 'booked',
        required: true,
      },
      {
        key: 'offeredSlot',
        label: 'offeredSlot',
        placeholder: 'optional',
      },
    ],
    buildRequest: (values) => ({
      path: `/makeups/${encodeURIComponent(values.id?.trim() || '')}/status`,
      body: {
        status: values.status?.trim() || '',
        offeredSlot: values.offeredSlot?.trim() || undefined,
      },
    }),
  },
  {
    id: 'makeupsBulkPatchStatus',
    title: 'Bulk Patch Status',
    description: 'Bulk status update for multiple makeups',
    category: 'Makeups Actions',
    method: 'PATCH',
    pathTemplate: '/makeups/bulk/status',
    fields: [
      {
        key: 'ids',
        label: 'ids (JSON array or csv)',
        defaultValue: '["id-1", "id-2"]',
        required: true,
        type: 'textarea',
      },
      {
        key: 'status',
        label: 'new status',
        defaultValue: 'booked',
        required: true,
      },
      {
        key: 'offeredSlot',
        label: 'offeredSlot',
        placeholder: 'optional',
      },
    ],
    buildRequest: (values) => ({
      path: '/makeups/bulk/status',
      body: {
        ids: parseJsonArray(values.ids || '[]'),
        status: values.status?.trim() || '',
        offeredSlot: values.offeredSlot?.trim() || undefined,
      },
    }),
  },
  {
    id: 'scheduleFlowsList',
    title: 'Schedule Flows (171+)',
    description: 'List flows and schedule coverage for operator planning',
    category: 'Расписание',
    method: 'GET',
    pathTemplate: '/ops/schedule/flows?minFlowNo=:minFlowNo',
    fields: [
      {
        key: 'minFlowNo',
        label: 'minFlowNo',
        defaultValue: '171',
      },
    ],
    buildRequest: (values) => ({
      path: '/ops/schedule/flows',
      query: {
        minFlowNo: values.minFlowNo?.trim() || '171',
      },
    }),
  },
  {
    id: 'scheduleLessonsList',
    title: 'Flow Schedule Lessons',
    description: 'Get schedule lessons for a concrete flow',
    category: 'Расписание',
    method: 'GET',
    pathTemplate: '/ops/schedule/flows/:flowNo/lessons?includeInactive=:includeInactive',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '171',
        required: true,
      },
      {
        key: 'includeInactive',
        label: 'includeInactive',
        defaultValue: 'false',
      },
    ],
    buildRequest: (values) => {
      const flowNo = values.flowNo?.trim() || '171'
      return {
        path: `/ops/schedule/flows/${encodeURIComponent(flowNo)}/lessons`,
        query: {
          includeInactive: values.includeInactive?.trim() || 'false',
        },
      }
    },
  },
  {
    id: 'scheduleLessonUpsert',
    title: 'Create/Upsert Lesson',
    description: 'Operator create or replace schedule lesson (endAt defaults to +2h)',
    category: 'Расписание',
    method: 'POST',
    pathTemplate: '/ops/schedule/flows/:flowNo/lessons',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '171',
        required: true,
      },
      {
        key: 'lessonNo',
        label: 'lessonNo',
        defaultValue: '3',
        required: true,
      },
      {
        key: 'startAt',
        label: 'startAt (ISO)',
        defaultValue: '2026-03-30T14:00:00.000Z',
        required: true,
      },
      {
        key: 'endAt',
        label: 'endAt (ISO, optional)',
      },
      {
        key: 'topic',
        label: 'topic',
        defaultValue: 'Lesson 3',
      },
    ],
    buildRequest: (values) => {
      const flowNo = values.flowNo?.trim() || '171'
      return {
        path: `/ops/schedule/flows/${encodeURIComponent(flowNo)}/lessons`,
        body: {
          lessonNo: Number(values.lessonNo?.trim() || '0'),
          startAt: values.startAt?.trim() || '',
          endAt: values.endAt?.trim() || undefined,
          topic: values.topic?.trim() || undefined,
        },
      }
    },
  },
  {
    id: 'scheduleLessonPatch',
    title: 'Edit Lesson',
    description: 'Operator patch schedule lesson values',
    category: 'Расписание',
    method: 'PATCH',
    pathTemplate: '/ops/schedule/flows/:flowNo/lessons/:lessonNo',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '171',
        required: true,
      },
      {
        key: 'lessonNo',
        label: 'lessonNo',
        defaultValue: '3',
        required: true,
      },
      {
        key: 'startAt',
        label: 'startAt (ISO, optional)',
      },
      {
        key: 'endAt',
        label: 'endAt (ISO, optional)',
      },
      {
        key: 'topic',
        label: 'topic (optional)',
      },
      {
        key: 'isActive',
        label: 'isActive (true/false, optional)',
      },
    ],
    buildRequest: (values) => {
      const flowNo = values.flowNo?.trim() || '171'
      const lessonNo = values.lessonNo?.trim() || '1'
      const rawActive = values.isActive?.trim().toLowerCase()
      const parsedIsActive = rawActive === 'true' ? true : rawActive === 'false' ? false : undefined

      return {
        path: `/ops/schedule/flows/${encodeURIComponent(flowNo)}/lessons/${encodeURIComponent(lessonNo)}`,
        body: {
          startAt: values.startAt?.trim() || undefined,
          endAt: values.endAt?.trim() || undefined,
          topic: values.topic?.trim() || undefined,
          isActive: parsedIsActive,
        },
      }
    },
  },
  {
    id: 'scheduleLessonDelete',
    title: 'Delete (Deactivate) Lesson',
    description: 'Soft-delete lesson from active schedule for operator',
    category: 'Расписание',
    method: 'DELETE',
    pathTemplate: '/ops/schedule/flows/:flowNo/lessons/:lessonNo',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '171',
        required: true,
      },
      {
        key: 'lessonNo',
        label: 'lessonNo',
        defaultValue: '3',
        required: true,
      },
    ],
    buildRequest: (values) => {
      const flowNo = values.flowNo?.trim() || '171'
      const lessonNo = values.lessonNo?.trim() || '1'

      return {
        path: `/ops/schedule/flows/${encodeURIComponent(flowNo)}/lessons/${encodeURIComponent(lessonNo)}`,
      }
    },
  },
  {
    id: 'scheduleAutofill',
    title: 'Autofill Weekly Schedule',
    description: 'Generate weekly lessons from first two lessons, then allow manual edits',
    category: 'Расписание',
    method: 'POST',
    pathTemplate: '/ops/schedule/flows/:flowNo/autofill',
    fields: [
      {
        key: 'flowNo',
        label: 'flowNo',
        defaultValue: '171',
        required: true,
      },
      {
        key: 'firstLessonNo',
        label: 'firstLessonNo',
        defaultValue: '1',
      },
      {
        key: 'secondLessonNo',
        label: 'secondLessonNo',
        defaultValue: '2',
      },
      {
        key: 'untilLessonNo',
        label: 'untilLessonNo',
        defaultValue: '12',
      },
      {
        key: 'overwriteExisting',
        label: 'overwriteExisting (true/false)',
        defaultValue: 'false',
      },
    ],
    buildRequest: (values) => {
      const flowNo = values.flowNo?.trim() || '171'
      return {
        path: `/ops/schedule/flows/${encodeURIComponent(flowNo)}/autofill`,
        body: {
          firstLessonNo: Number(values.firstLessonNo?.trim() || '1'),
          secondLessonNo: Number(values.secondLessonNo?.trim() || '2'),
          untilLessonNo: Number(values.untilLessonNo?.trim() || '12'),
          overwriteExisting: values.overwriteExisting?.trim().toLowerCase() === 'true',
        },
      }
    },
  },
  {
    id: 'scheduleImportViaOps',
    title: 'Import Schedule from Sheets',
    description: 'Operator-triggered schedule import from Google Sheets',
    category: 'Расписание',
    method: 'POST',
    pathTemplate: '/ops/schedule/import?dryRun=:dryRun&flowNo=:flowNo',
    fields: [
      {
        key: 'dryRun',
        label: 'dryRun',
        defaultValue: 'true',
      },
      {
        key: 'flowNo',
        label: 'flowNo (optional)',
      },
    ],
    buildRequest: (values) => ({
      path: '/ops/schedule/import',
      query: {
        dryRun: values.dryRun?.trim() || 'true',
        flowNo: values.flowNo?.trim() || undefined,
      },
    }),
  },
  {
    id: 'crmStudentsList',
    title: 'CRM Students List',
    description: 'List CRM students with filters/pagination',
    category: 'Ученики',
    method: 'GET',
    pathTemplate: '/ops/crm/students?q=:q&flowNo=:flowNo&page=:page&limit=:limit',
    fields: [
      { key: 'q', label: 'q' },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'status', label: 'status' },
      { key: 'paymentStatus', label: 'paymentStatus' },
      { key: 'page', label: 'page', defaultValue: '1' },
      { key: 'limit', label: 'limit', defaultValue: '25' },
    ],
    buildRequest: (values) => ({
      path: '/ops/crm/students',
      query: {
        q: values.q?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        status: values.status?.trim() || undefined,
        paymentStatus: values.paymentStatus?.trim() || undefined,
        page: values.page?.trim() || '1',
        limit: values.limit?.trim() || '25',
      },
    }),
  },
  {
    id: 'crmStudentUpsert',
    title: 'CRM Upsert Student',
    description: 'Create/update CRM student by phone',
    category: 'Ученики',
    method: 'POST',
    pathTemplate: '/ops/crm/students',
    fields: [
      { key: 'fullName', label: 'fullName', required: true },
      { key: 'phone', label: 'phone', required: true },
      { key: 'telegramId', label: 'telegramId' },
      { key: 'status', label: 'status', defaultValue: 'active' },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'paymentStatus', label: 'paymentStatus', defaultValue: 'reserved' },
    ],
    buildRequest: (values) => ({
      path: '/ops/crm/students',
      body: {
        fullName: values.fullName?.trim() || '',
        phone: values.phone?.trim() || '',
        telegramId: values.telegramId?.trim() || undefined,
        status: values.status?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        paymentStatus: values.paymentStatus?.trim() || undefined,
      },
    }),
  },
  {
    id: 'crmFlowsList',
    title: 'CRM Flows List',
    description: 'List CRM flows with student counters',
    category: 'Группы',
    method: 'GET',
    pathTemplate: '/ops/crm/flows',
    fields: [
      { key: 'minFlowNo', label: 'minFlowNo', defaultValue: '171' },
      { key: 'q', label: 'q' },
      { key: 'page', label: 'page', defaultValue: '1' },
      { key: 'limit', label: 'limit', defaultValue: '25' },
    ],
    buildRequest: (values) => ({
      path: '/ops/crm/flows',
      query: {
        minFlowNo: values.minFlowNo?.trim() || '171',
        q: values.q?.trim() || undefined,
        page: values.page?.trim() || '1',
        limit: values.limit?.trim() || '25',
      },
    }),
  },
  {
    id: 'crmFlowAttachStudent',
    title: 'CRM Attach Student To Flow',
    description: 'Create enrollment for student in flow',
    category: 'Группы',
    method: 'POST',
    pathTemplate: '/ops/crm/flows/:flowNo/students',
    fields: [
      { key: 'flowNo', label: 'flowNo', required: true },
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'paymentStatus', label: 'paymentStatus', defaultValue: 'reserved' },
    ],
    buildRequest: (values) => ({
      path: `/ops/crm/flows/${encodeURIComponent(values.flowNo?.trim() || '')}/students`,
      body: {
        studentId: values.studentId?.trim() || '',
        paymentStatus: values.paymentStatus?.trim() || undefined,
      },
    }),
  },
  {
    id: 'crmPaymentStagesList',
    title: 'CRM Payment Stages List',
    description: 'List payment stages for student',
    category: 'Оплаты',
    method: 'GET',
    pathTemplate: '/ops/crm/students/:studentId/payment-stages',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowNo', label: 'flowNo' },
    ],
    buildRequest: (values) => ({
      path: `/ops/crm/students/${encodeURIComponent(values.studentId?.trim() || '')}/payment-stages`,
      query: {
        flowNo: values.flowNo?.trim() || undefined,
      },
    }),
  },
  {
    id: 'crmPaymentStageCreate',
    title: 'CRM Create Payment Stage',
    description: 'Add payment stage for student',
    category: 'Оплаты',
    method: 'POST',
    pathTemplate: '/ops/crm/students/:studentId/payment-stages',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'stageNo', label: 'stageNo', defaultValue: '1' },
      { key: 'title', label: 'title', defaultValue: 'Stage 1' },
      { key: 'amount', label: 'amount', defaultValue: '100000' },
      { key: 'status', label: 'status', defaultValue: 'pending' },
      { key: 'dueDate', label: 'dueDate YYYY-MM-DD' },
    ],
    buildRequest: (values) => ({
      path: `/ops/crm/students/${encodeURIComponent(values.studentId?.trim() || '')}/payment-stages`,
      body: {
        flowNo: values.flowNo?.trim() || undefined,
        stageNo: Number(values.stageNo?.trim() || '1'),
        title: values.title?.trim() || 'Stage 1',
        amount: values.amount?.trim() || '0',
        status: values.status?.trim() || 'pending',
        dueDate: values.dueDate?.trim() || undefined,
      },
    }),
  },
  {
    id: 'crmCommentsList',
    title: 'CRM Comments List',
    description: 'List comments for student',
    category: 'Комментарии',
    method: 'GET',
    pathTemplate: '/ops/crm/students/:studentId/comments',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowNo', label: 'flowNo' },
    ],
    buildRequest: (values) => ({
      path: `/ops/crm/students/${encodeURIComponent(values.studentId?.trim() || '')}/comments`,
      query: {
        flowNo: values.flowNo?.trim() || undefined,
      },
    }),
  },
  {
    id: 'crmCommentCreate',
    title: 'CRM Create Comment',
    description: 'Create student comment',
    category: 'Комментарии',
    method: 'POST',
    pathTemplate: '/ops/crm/students/:studentId/comments',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'comment', label: 'comment', required: true, type: 'textarea' },
      { key: 'commentType', label: 'commentType', defaultValue: 'general' },
    ],
    buildRequest: (values) => ({
      path: `/ops/crm/students/${encodeURIComponent(values.studentId?.trim() || '')}/comments`,
      body: {
        flowNo: values.flowNo?.trim() || undefined,
        comment: values.comment?.trim() || '',
        commentType: values.commentType?.trim() || 'general',
      },
    }),
  },
  {
    id: 'crmImportPreview',
    title: 'CRM Import Preview',
    description: 'Preview Sheets clients import',
    category: 'Импорт из Sheets',
    method: 'POST',
    pathTemplate: '/ops/crm/import/sheets/clients/preview',
    fields: [
      { key: 'mode', label: 'mode', defaultValue: 'flows' },
      { key: 'flowNos', label: 'flowNos CSV', defaultValue: '171,172' },
      { key: 'upsertPolicy', label: 'upsertPolicy', defaultValue: 'phone_first' },
    ],
    buildRequest: (values) => ({
      path: '/ops/crm/import/sheets/clients/preview',
      body: {
        mode: (values.mode?.trim() || 'flows') as 'flows' | 'students',
        flowNos: parseJsonArray(values.flowNos || ''),
        upsertPolicy: (values.upsertPolicy?.trim() || 'phone_first') as
          | 'phone_first'
          | 'strict_phone',
      },
    }),
  },
  {
    id: 'crmImportApply',
    title: 'CRM Import Apply',
    description: 'Apply Sheets clients import',
    category: 'Импорт из Sheets',
    method: 'POST',
    pathTemplate: '/ops/crm/import/sheets/clients/apply',
    fields: [
      { key: 'mode', label: 'mode', defaultValue: 'flows' },
      { key: 'flowNos', label: 'flowNos CSV', defaultValue: '171,172' },
      {
        key: 'idempotencyKey',
        label: 'idempotencyKey',
        defaultValue: 'dashboard-manual-1',
      },
      { key: 'upsertPolicy', label: 'upsertPolicy', defaultValue: 'phone_first' },
    ],
    buildRequest: (values) => ({
      path: '/ops/crm/import/sheets/clients/apply',
      body: {
        mode: (values.mode?.trim() || 'flows') as 'flows' | 'students',
        flowNos: parseJsonArray(values.flowNos || ''),
        idempotencyKey: values.idempotencyKey?.trim() || 'dashboard-manual-1',
        upsertPolicy: (values.upsertPolicy?.trim() || 'phone_first') as
          | 'phone_first'
          | 'strict_phone',
      },
    }),
  },
  {
    id: 'referralCodeCreate',
    title: 'Referral Create Code',
    description: 'Create referral code for existing student',
    category: 'Рефералы',
    method: 'POST',
    pathTemplate: '/ops/voice/referrals/codes',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'discountPct', label: 'discountPct', defaultValue: '10', required: true },
      { key: 'code', label: 'custom code (optional)' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/referrals/codes',
      body: {
        studentId: values.studentId?.trim() || '',
        discountPct: Number(values.discountPct?.trim() || '10'),
        code: values.code?.trim() || undefined,
      },
    }),
  },
  {
    id: 'referralCodePreview',
    title: 'Referral Public Preview',
    description: 'Public code preview for invited lead',
    category: 'Рефералы',
    method: 'GET',
    pathTemplate: '/public/voice/referrals/:code',
    fields: [{ key: 'code', label: 'code', required: true }],
    buildRequest: (values) => ({
      path: `/public/voice/referrals/${encodeURIComponent(values.code?.trim() || '')}`,
    }),
  },
  {
    id: 'referralPublicFlows',
    title: 'Referral Public Flows',
    description: 'List flows available for referral booking',
    category: 'Рефералы',
    method: 'GET',
    pathTemplate: '/public/voice/referrals/flows?minFlowNo=:minFlowNo&limit=:limit',
    fields: [
      { key: 'minFlowNo', label: 'minFlowNo', defaultValue: '171' },
      { key: 'limit', label: 'limit', defaultValue: '30' },
    ],
    buildRequest: (values) => ({
      path: '/public/voice/referrals/flows',
      query: {
        minFlowNo: values.minFlowNo?.trim() || '171',
        limit: values.limit?.trim() || '30',
      },
    }),
  },
  {
    id: 'referralPublicBooking',
    title: 'Referral Public Booking',
    description: 'Create booking from referral code for lead',
    category: 'Рефералы',
    method: 'POST',
    pathTemplate: '/public/voice/referrals/bookings',
    fields: [
      { key: 'code', label: 'code', required: true },
      { key: 'fullName', label: 'fullName', required: true },
      { key: 'phone', label: 'phone', required: true },
      { key: 'flowNo', label: 'flowNo', required: true },
    ],
    buildRequest: (values) => ({
      path: '/public/voice/referrals/bookings',
      body: {
        code: values.code?.trim() || '',
        fullName: values.fullName?.trim() || '',
        phone: values.phone?.trim() || '',
        flowNo: values.flowNo?.trim() || '',
      },
    }),
  },
  {
    id: 'referralConfirmPaid',
    title: 'Referral Confirm Paid',
    description: 'Confirm referral conversion and award VP',
    category: 'Рефералы',
    method: 'POST',
    pathTemplate: '/ops/voice/referrals/uses/:id/confirm-paid',
    fields: [{ key: 'id', label: 'referral use id', required: true }],
    buildRequest: (values) => ({
      path: `/ops/voice/referrals/uses/${encodeURIComponent(values.id?.trim() || '')}/confirm-paid`,
      body: {},
    }),
  },
  {
    id: 'referralUsesList',
    title: 'Referral Uses List',
    description: 'List referral uses by student role',
    category: 'Рефералы',
    method: 'GET',
    pathTemplate:
      '/ops/voice/referrals/students/:studentId/uses?role=:role&status=:status&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'role', label: 'role', defaultValue: 'referrer' },
      { key: 'status', label: 'status' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/referrals/students/${encodeURIComponent(values.studentId?.trim() || '')}/uses`,
      query: {
        role: values.role?.trim() || 'referrer',
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'onboardingGraduateStart',
    title: 'Graduate Onboarding Start',
    description: 'Run graduate onboarding pack: VP completion, intro, club entry and referral link',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate:
      '/ops/voice/onboarding/graduates/start',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowId', label: 'flowId' },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'referralDiscountPct', label: 'referralDiscountPct', defaultValue: '10' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'source', label: 'source', defaultValue: 'operator' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/start',
      body: {
        studentId: values.studentId?.trim() || '',
        flowId: values.flowId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        referralDiscountPct: values.referralDiscountPct?.trim() || '10',
        asOf: values.asOf?.trim() || undefined,
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateProgress',
    title: 'Graduate Onboarding Progress',
    description: 'Fetch persisted onboarding progress and step markers for student/flow',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate: '/ops/voice/onboarding/graduates/progress?studentId=:studentId&flowId=:flowId&flowNo=:flowNo',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowId', label: 'flowId' },
      { key: 'flowNo', label: 'flowNo' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/progress',
      query: {
        studentId: values.studentId?.trim() || '',
        flowId: values.flowId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateProgressList',
    title: 'Graduate Onboarding Progress List',
    description: 'List onboarding progress by flow/student/status with funnel summary',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate:
      '/ops/voice/onboarding/graduates/progress/list?flowNo=:flowNo&studentId=:studentId&status=:status&limit=:limit&includeTimeline=:includeTimeline',
    fields: [
      { key: 'flowNo', label: 'flowNo' },
      { key: 'studentId', label: 'studentId' },
      { key: 'status', label: 'status (in_progress|completed)' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
      { key: 'includeTimeline', label: 'includeTimeline', defaultValue: 'false' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/progress/list',
      query: {
        flowNo: values.flowNo?.trim() || undefined,
        studentId: values.studentId?.trim() || undefined,
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
        includeTimeline: values.includeTimeline?.trim() || 'false',
      },
    }),
  },
  {
    id: 'onboardingGraduateStepMark',
    title: 'Graduate Onboarding Step Mark',
    description: 'Mark onboarding step as read/completed and persist deterministic progress',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/onboarding/graduates/step/mark',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowId', label: 'flowId' },
      { key: 'flowNo', label: 'flowNo' },
      {
        key: 'stepKey',
        label: 'stepKey',
        required: true,
        defaultValue: 'step2VpIntro',
      },
      {
        key: 'state',
        label: 'state',
        required: true,
        defaultValue: 'read',
      },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'source', label: 'source', defaultValue: 'operator' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/step/mark',
      body: {
        studentId: values.studentId?.trim() || '',
        flowId: values.flowId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        stepKey: values.stepKey?.trim() || 'step2VpIntro',
        state: values.state?.trim() || 'read',
        asOf: values.asOf?.trim() || undefined,
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeSend',
    title: 'Graduate Onboarding Nudge Send',
    description: 'Dispatch onboarding nudge to selected channels with idempotency keying',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/onboarding/graduates/nudges/send',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowId', label: 'flowId' },
      { key: 'flowNo', label: 'flowNo' },
      {
        key: 'stepKey',
        label: 'stepKey',
        required: true,
        defaultValue: 'step2VpIntro',
      },
      {
        key: 'channels',
        label: 'channels json/csv',
        defaultValue: '["telegram"]',
      },
      {
        key: 'message',
        label: 'message',
        required: true,
        type: 'textarea',
      },
      { key: 'stalledHours', label: 'stalledHours', defaultValue: '0' },
      { key: 'campaignKey', label: 'campaignKey' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'source', label: 'source', defaultValue: 'operator' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/nudges/send',
      body: {
        studentId: values.studentId?.trim() || '',
        flowId: values.flowId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        stepKey: values.stepKey?.trim() || 'step2VpIntro',
        channels: values.channels?.trim() ? parseJsonArray(values.channels) : ['telegram'],
        message: values.message?.trim() || '',
        stalledHours: values.stalledHours?.trim() || '0',
        campaignKey: values.campaignKey?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeRetry',
    title: 'Graduate Onboarding Nudge Retry',
    description: 'Retry nudge dispatch for one channel with force campaignKey',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/onboarding/graduates/nudges/retry',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'flowId', label: 'flowId' },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'stepKey', label: 'stepKey', required: true, defaultValue: 'step2VpIntro' },
      { key: 'channel', label: 'channel', required: true, defaultValue: 'telegram' },
      { key: 'message', label: 'message', type: 'textarea' },
      { key: 'stalledHours', label: 'stalledHours', defaultValue: '0' },
      { key: 'campaignKey', label: 'campaignKey' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'source', label: 'source', defaultValue: 'operator' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/nudges/retry',
      body: {
        studentId: values.studentId?.trim() || '',
        flowId: values.flowId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        stepKey: values.stepKey?.trim() || 'step2VpIntro',
        channel: values.channel?.trim() || 'telegram',
        message: values.message?.trim() || undefined,
        stalledHours: values.stalledHours?.trim() || '0',
        campaignKey: values.campaignKey?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeHistory',
    title: 'Graduate Onboarding Nudge History',
    description: 'List sent/skipped onboarding nudges by student/flow/channel/state',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate:
      '/ops/voice/onboarding/graduates/nudges/history?studentId=:studentId&flowNo=:flowNo&stepKey=:stepKey&channel=:channel&state=:state&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId' },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'stepKey', label: 'stepKey' },
      { key: 'channel', label: 'channel (telegram|crm)' },
      { key: 'state', label: 'state (sent|skipped_duplicate)' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/nudges/history',
      query: {
        studentId: values.studentId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        stepKey: values.stepKey?.trim() || undefined,
        channel: values.channel?.trim() || undefined,
        state: values.state?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeOutbox',
    title: 'Graduate Onboarding Nudge Outbox',
    description: 'Inspect onboarding nudge outbox state by student/flow/channel/state',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate:
      '/ops/voice/onboarding/graduates/nudges/outbox?studentId=:studentId&flowNo=:flowNo&channel=:channel&state=:state&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId' },
      { key: 'flowNo', label: 'flowNo' },
      { key: 'channel', label: 'channel (telegram|crm)' },
      { key: 'state', label: 'state (pending|sent|failed|canceled)' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/nudges/outbox',
      query: {
        studentId: values.studentId?.trim() || undefined,
        flowNo: values.flowNo?.trim() || undefined,
        channel: values.channel?.trim() || undefined,
        state: values.state?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeOutboxDispatch',
    title: 'Graduate Nudge Outbox Dispatch',
    description: 'Dispatch one onboarding nudge outbox item by id',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/onboarding/graduates/nudges/outbox/dispatch',
    fields: [
      { key: 'outboxId', label: 'outboxId', required: true },
      { key: 'source', label: 'source', defaultValue: 'operator' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/nudges/outbox/dispatch',
      body: {
        outboxId: values.outboxId?.trim() || '',
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeOutboxDispatchSweep',
    title: 'Graduate Nudge Outbox Sweep',
    description: 'Dispatch pending/failed onboarding nudge outbox items in batch',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/onboarding/graduates/nudges/outbox/dispatch-sweep',
    fields: [
      { key: 'limit', label: 'limit', defaultValue: '50' },
      { key: 'channel', label: 'channel (telegram|crm)' },
      { key: 'source', label: 'source', defaultValue: 'system' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/onboarding/graduates/nudges/outbox/dispatch-sweep',
      body: {
        limit: values.limit?.trim() || '50',
        channel: values.channel?.trim() || undefined,
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'onboardingGraduateNudgeOutboxWorkerStatus',
    title: 'Graduate Nudge Outbox Worker Status',
    description: 'Get onboarding nudge outbox worker runtime status, queue lag, and alerts',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate: '/ops/voice/onboarding/graduates/nudges/outbox/worker/status',
    fields: [],
    buildRequest: () => ({
      path: '/ops/voice/onboarding/graduates/nudges/outbox/worker/status',
    }),
  },
  {
    id: 'voiceNotificationWorkersStatus',
    title: 'Voice Notification Workers Status',
    description: 'Get consolidated status and alerts for onboarding and retention workers',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate: '/ops/voice/notifications/workers/status',
    fields: [],
    buildRequest: () => ({
      path: '/ops/voice/notifications/workers/status',
    }),
  },
  {
    id: 'voiceNotificationWorkersAlertsCheck',
    title: 'Voice Workers Alerts Check',
    description: 'Run alert check and write deduplicated alert entries to audit log',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/notifications/workers/alerts/check',
    fields: [{ key: 'dedupMinutes', label: 'dedupMinutes', defaultValue: '60' }],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/workers/alerts/check',
      body: {
        dedupMinutes: values.dedupMinutes?.trim() || '60',
      },
    }),
  },
  {
    id: 'voiceNotificationWorkersAlertsHistory',
    title: 'Voice Workers Alerts History',
    description: 'Get recent worker alert incidents from audit log',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate: '/ops/voice/notifications/workers/alerts/history?limit=:limit',
    fields: [{ key: 'limit', label: 'limit', defaultValue: '50' }],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/workers/alerts/history',
      query: {
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'voiceNotificationWorkersIncidents',
    title: 'Voice Workers Incidents',
    description: 'List worker incidents with status, scope and SLA metrics',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate:
      '/ops/voice/notifications/workers/incidents?status=:status&scope=:scope&q=:q&limit=:limit',
    fields: [
      { key: 'status', label: 'status (open|acknowledged|resolved)' },
      { key: 'scope', label: 'scope (onboarding_nudge|telegram_retention)' },
      { key: 'q', label: 'q (alert/code search)' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/workers/incidents',
      query: {
        status: values.status?.trim() || undefined,
        scope: values.scope?.trim() || undefined,
        q: values.q?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'voiceNotificationWorkersIncidentsAck',
    title: 'Voice Workers Incident Ack',
    description: 'Acknowledge worker incident',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/notifications/workers/incidents/ack',
    fields: [
      { key: 'alert', label: 'alert key', required: true },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/workers/incidents/ack',
      body: {
        alert: values.alert?.trim() || '',
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'voiceNotificationWorkersIncidentsResolve',
    title: 'Voice Workers Incident Resolve',
    description: 'Resolve worker incident',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/notifications/workers/incidents/resolve',
    fields: [
      { key: 'alert', label: 'alert key', required: true },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/workers/incidents/resolve',
      body: {
        alert: values.alert?.trim() || '',
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'voiceNotificationWorkersEscalationStatus',
    title: 'Voice Workers Escalation Status',
    description: 'Get escalation worker status and thresholds',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate: '/ops/voice/notifications/workers/escalation/status',
    fields: [],
    buildRequest: () => ({
      path: '/ops/voice/notifications/workers/escalation/status',
    }),
  },
  {
    id: 'voiceNotificationWorkersEscalationRun',
    title: 'Voice Workers Escalation Run',
    description: 'Run incident escalation sweep manually',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/notifications/workers/escalation/run',
    fields: [
      { key: 'maxPerRun', label: 'maxPerRun', defaultValue: '20' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/workers/escalation/run',
      body: {
        maxPerRun: values.maxPerRun?.trim() || '20',
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'telegramRetentionEvents',
    title: 'Telegram Retention Events',
    description: 'List retention events queue for Telegram delivery channel',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate:
      '/ops/voice/notifications/telegram/retention/events?studentId=:studentId&eventType=:eventType&state=:state&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId' },
      {
        key: 'eventType',
        label:
          'eventType (vp.transaction.v1|vp.level.progress.v1|referral.conversion.paid.v1|conference.early.booking.open.v1)',
      },
      { key: 'state', label: 'state (pending|sent|failed|skipped_duplicate)' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/telegram/retention/events',
      query: {
        studentId: values.studentId?.trim() || undefined,
        eventType: values.eventType?.trim() || undefined,
        state: values.state?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'telegramRetentionDispatch',
    title: 'Telegram Retention Dispatch',
    description: 'Dispatch one Telegram retention event by idempotency key',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/notifications/telegram/retention/dispatch',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      {
        key: 'eventType',
        label: 'eventType',
        required: true,
        defaultValue: 'vp.transaction.v1',
      },
      { key: 'idempotencyKey', label: 'idempotencyKey', required: true },
      { key: 'source', label: 'source', defaultValue: 'operator' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/telegram/retention/dispatch',
      body: {
        studentId: values.studentId?.trim() || '',
        eventType: values.eventType?.trim() || 'vp.transaction.v1',
        idempotencyKey: values.idempotencyKey?.trim() || '',
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'telegramRetentionDispatchSweep',
    title: 'Telegram Retention Dispatch Sweep',
    description: 'Dispatch pending retention events in batch',
    category: 'Онбординг',
    method: 'POST',
    pathTemplate: '/ops/voice/notifications/telegram/retention/dispatch-sweep',
    fields: [
      { key: 'limit', label: 'limit', defaultValue: '50' },
      { key: 'source', label: 'source', defaultValue: 'system' },
      { key: 'actor', label: 'actor', defaultValue: 'ops-web' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/telegram/retention/dispatch-sweep',
      body: {
        limit: values.limit?.trim() || '50',
        source: values.source?.trim() || undefined,
        actor: values.actor?.trim() || undefined,
      },
    }),
  },
  {
    id: 'telegramRetentionOutbox',
    title: 'Telegram Retention Outbox',
    description: 'Inspect notification outbox state for Telegram retention channel',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate:
      '/ops/voice/notifications/telegram/retention/outbox?state=:state&limit=:limit',
    fields: [
      { key: 'state', label: 'state (pending|sent|failed|canceled)' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/notifications/telegram/retention/outbox',
      query: {
        state: values.state?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'telegramRetentionOutboxWorkerStatus',
    title: 'Telegram Retention Worker Status',
    description: 'Get Telegram retention outbox worker runtime status, queue lag, and alerts',
    category: 'Онбординг',
    method: 'GET',
    pathTemplate: '/ops/voice/notifications/telegram/retention/worker/status',
    fields: [],
    buildRequest: () => ({
      path: '/ops/voice/notifications/telegram/retention/worker/status',
    }),
  },
  {
    id: 'superpowerGoalCreate',
    title: 'Superpower Goal Create',
    description: 'Create Superpower club goal for student',
    category: 'Суперсильные',
    method: 'POST',
    pathTemplate: '/ops/voice/superpower/goals',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'title', label: 'title', required: true },
      { key: 'description', label: 'description' },
      { key: 'targetAt', label: 'targetAt ISO datetime' },
      { key: 'priority', label: 'priority 1-5', defaultValue: '3' },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/superpower/goals',
      body: {
        studentId: values.studentId?.trim() || '',
        title: values.title?.trim() || '',
        description: values.description?.trim() || undefined,
        targetAt: values.targetAt?.trim() || undefined,
        priority: values.priority?.trim() || '3',
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'superpowerGoalsList',
    title: 'Superpower Goals List',
    description: 'List Superpower goals by student/status',
    category: 'Суперсильные',
    method: 'GET',
    pathTemplate: '/ops/voice/superpower/goals?studentId=:studentId&status=:status&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId' },
      { key: 'status', label: 'status', defaultValue: 'active' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/superpower/goals',
      query: {
        studentId: values.studentId?.trim() || undefined,
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'superpowerGoalUpdate',
    title: 'Superpower Goal Update',
    description: 'Update Superpower goal status or details',
    category: 'Суперсильные',
    method: 'PATCH',
    pathTemplate: '/ops/voice/superpower/goals/:id',
    fields: [
      { key: 'id', label: 'goal id', required: true },
      { key: 'title', label: 'title' },
      { key: 'description', label: 'description' },
      { key: 'status', label: 'status' },
      { key: 'targetAt', label: 'targetAt ISO datetime' },
      { key: 'priority', label: 'priority 1-5' },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/superpower/goals/${encodeURIComponent(values.id?.trim() || '')}`,
      body: {
        title: values.title?.trim() || undefined,
        description: values.description?.trim() || undefined,
        status: values.status?.trim() || undefined,
        targetAt: values.targetAt?.trim() || undefined,
        priority: values.priority?.trim() || undefined,
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'superpowerMeetingCreate',
    title: 'Superpower Meeting Create',
    description: 'Create mastermind meeting for Superpower club',
    category: 'Суперсильные',
    method: 'POST',
    pathTemplate: '/ops/voice/superpower/meetings',
    fields: [
      { key: 'title', label: 'title', required: true },
      { key: 'startsAt', label: 'startsAt ISO datetime', required: true },
      { key: 'endsAt', label: 'endsAt ISO datetime' },
      { key: 'format', label: 'format', defaultValue: 'online' },
      { key: 'status', label: 'status', defaultValue: 'scheduled' },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/superpower/meetings',
      body: {
        title: values.title?.trim() || '',
        startsAt: values.startsAt?.trim() || '',
        endsAt: values.endsAt?.trim() || undefined,
        format: values.format?.trim() || 'online',
        status: values.status?.trim() || 'scheduled',
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'superpowerMeetingsList',
    title: 'Superpower Meetings List',
    description: 'List Superpower meetings with filters',
    category: 'Суперсильные',
    method: 'GET',
    pathTemplate: '/ops/voice/superpower/meetings?status=:status&from=:from&to=:to&limit=:limit',
    fields: [
      { key: 'status', label: 'status' },
      { key: 'from', label: 'from ISO datetime' },
      { key: 'to', label: 'to ISO datetime' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/superpower/meetings',
      query: {
        status: values.status?.trim() || undefined,
        from: values.from?.trim() || undefined,
        to: values.to?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'superpowerMeetingClose',
    title: 'Superpower Meeting Close',
    description: 'Close meeting after completion',
    category: 'Суперсильные',
    method: 'POST',
    pathTemplate: '/ops/voice/superpower/meetings/:id/close',
    fields: [
      { key: 'id', label: 'meeting id', required: true },
      { key: 'note', label: 'note' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/superpower/meetings/${encodeURIComponent(values.id?.trim() || '')}/close`,
      body: {
        note: values.note?.trim() || undefined,
      },
    }),
  },
  {
    id: 'superpowerParticipantAdd',
    title: 'Superpower Participant Add',
    description: 'Add meeting participant and optionally award VP',
    category: 'Суперсильные',
    method: 'POST',
    pathTemplate: '/ops/voice/superpower/meetings/:id/participants',
    fields: [
      { key: 'id', label: 'meeting id', required: true },
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'role', label: 'role', defaultValue: 'hot_seat', required: true },
      { key: 'awardVp', label: 'awardVp true/false', defaultValue: 'true' },
      { key: 'note', label: 'note' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/superpower/meetings/${encodeURIComponent(values.id?.trim() || '')}/participants`,
      body: {
        studentId: values.studentId?.trim() || '',
        role: values.role?.trim() || 'hot_seat',
        awardVp: values.awardVp?.trim().toLowerCase() !== 'false',
        note: values.note?.trim() || undefined,
      },
    }),
  },
  {
    id: 'superpowerParticipantsList',
    title: 'Superpower Participants List',
    description: 'List participants for meeting',
    category: 'Суперсильные',
    method: 'GET',
    pathTemplate: '/ops/voice/superpower/meetings/:id/participants',
    fields: [{ key: 'id', label: 'meeting id', required: true }],
    buildRequest: (values) => ({
      path: `/ops/voice/superpower/meetings/${encodeURIComponent(values.id?.trim() || '')}/participants`,
    }),
  },
  {
    id: 'vpAnalyticsSummary',
    title: 'VP Analytics Summary',
    description: 'Emission vs stock monthly summary with inflation signal',
    category: 'VP Экономика',
    method: 'GET',
    pathTemplate: '/ops/voice/analytics/vp/summary?asOf=:asOf&months=:months',
    fields: [
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'months', label: 'months', defaultValue: '6' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/analytics/vp/summary',
      query: {
        asOf: values.asOf?.trim() || undefined,
        months: values.months?.trim() || '6',
      },
    }),
  },
  {
    id: 'vpAnalyticsLeaderboard',
    title: 'VP Analytics Leaderboard',
    description: 'Top participants and top VIP council candidates by accumulated VP',
    category: 'VP Экономика',
    method: 'GET',
    pathTemplate: '/ops/voice/analytics/vp/leaderboard?limit=:limit',
    fields: [{ key: 'limit', label: 'limit', defaultValue: '20' }],
    buildRequest: (values) => ({
      path: '/ops/voice/analytics/vp/leaderboard',
      query: {
        limit: values.limit?.trim() || '20',
      },
    }),
  },
  {
    id: 'vpAnalyticsHqSnapshot',
    title: 'VP Analytics HQ Snapshot',
    description: 'HQ snapshot with core operational KPIs and VP economy state',
    category: 'VP Экономика',
    method: 'GET',
    pathTemplate: '/ops/voice/analytics/hq/snapshot?asOf=:asOf',
    fields: [{ key: 'asOf', label: 'asOf ISO datetime' }],
    buildRequest: (values) => ({
      path: '/ops/voice/analytics/hq/snapshot',
      query: {
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpAnalyticsOwnerDashboard',
    title: 'VP Analytics Owner Dashboard',
    description:
      'Owner-focused VP dashboard: emission, store stock, inflation warning and top participants',
    category: 'VP Экономика',
    method: 'GET',
    pathTemplate: '/ops/voice/analytics/owner/vp-dashboard?asOf=:asOf&months=:months&limit=:limit',
    fields: [
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'months', label: 'months', defaultValue: '6' },
      { key: 'limit', label: 'limit', defaultValue: '20' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/analytics/owner/vp-dashboard',
      query: {
        asOf: values.asOf?.trim() || undefined,
        months: values.months?.trim() || '6',
        limit: values.limit?.trim() || '20',
      },
    }),
  },
  {
    id: 'councilElectionCreate',
    title: 'Council Election Create',
    description: 'Create council election window',
    category: 'Совет',
    method: 'POST',
    pathTemplate: '/ops/voice/council/elections',
    fields: [
      { key: 'startAt', label: 'startAt ISO datetime', required: true },
      { key: 'endAt', label: 'endAt ISO datetime', required: true },
      { key: 'seats', label: 'seats', defaultValue: '20' },
      { key: 'status', label: 'status', defaultValue: 'draft' },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/council/elections',
      body: {
        startAt: values.startAt?.trim() || '',
        endAt: values.endAt?.trim() || '',
        seats: values.seats?.trim() || undefined,
        status: values.status?.trim() || undefined,
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'councilElectionsList',
    title: 'Council Elections List',
    description: 'List council elections',
    category: 'Совет',
    method: 'GET',
    pathTemplate: '/ops/voice/council/elections?status=:status&limit=:limit',
    fields: [
      { key: 'status', label: 'status' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/council/elections',
      query: {
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'councilElectionStatusUpdate',
    title: 'Council Election Status Update',
    description: 'Update election status draft/active/closed/finalized',
    category: 'Совет',
    method: 'PATCH',
    pathTemplate: '/ops/voice/council/elections/:id/status',
    fields: [
      { key: 'id', label: 'election id', required: true },
      { key: 'status', label: 'status', required: true },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/council/elections/${encodeURIComponent(values.id?.trim() || '')}/status`,
      body: {
        status: values.status?.trim() || '',
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'councilCandidatesList',
    title: 'Council Candidates List',
    description: 'List eligible VIP candidates by accumulated VP',
    category: 'Совет',
    method: 'GET',
    pathTemplate: '/ops/voice/council/elections/:id/candidates?limit=:limit',
    fields: [
      { key: 'id', label: 'election id', required: true },
      { key: 'limit', label: 'limit', defaultValue: '20' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/council/elections/${encodeURIComponent(values.id?.trim() || '')}/candidates`,
      query: {
        limit: values.limit?.trim() || '20',
      },
    }),
  },
  {
    id: 'councilVoteCast',
    title: 'Council Vote Cast',
    description: 'Cast 1 VP vote for candidate in active election',
    category: 'Совет',
    method: 'POST',
    pathTemplate: '/ops/voice/council/elections/:id/votes',
    fields: [
      { key: 'id', label: 'election id', required: true },
      { key: 'voterId', label: 'voterId', required: true },
      { key: 'candidateId', label: 'candidateId', required: true },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/council/elections/${encodeURIComponent(values.id?.trim() || '')}/votes`,
      body: {
        voterId: values.voterId?.trim() || '',
        candidateId: values.candidateId?.trim() || '',
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'councilVotesList',
    title: 'Council Votes List',
    description: 'List votes for election',
    category: 'Совет',
    method: 'GET',
    pathTemplate: '/ops/voice/council/elections/:id/votes?limit=:limit',
    fields: [
      { key: 'id', label: 'election id', required: true },
      { key: 'limit', label: 'limit', defaultValue: '100' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/council/elections/${encodeURIComponent(values.id?.trim() || '')}/votes`,
      query: {
        limit: values.limit?.trim() || '100',
      },
    }),
  },
  {
    id: 'councilElectionFinalize',
    title: 'Council Election Finalize',
    description: 'Finalize election and create council members term',
    category: 'Совет',
    method: 'POST',
    pathTemplate: '/ops/voice/council/elections/:id/finalize',
    fields: [
      { key: 'id', label: 'election id', required: true },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/council/elections/${encodeURIComponent(values.id?.trim() || '')}/finalize`,
      body: {
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'councilMembersList',
    title: 'Council Members List',
    description: 'List council members and current term',
    category: 'Совет',
    method: 'GET',
    pathTemplate: '/ops/voice/council/members?electionId=:electionId&currentOnly=:currentOnly&asOf=:asOf&limit=:limit',
    fields: [
      { key: 'electionId', label: 'electionId' },
      { key: 'currentOnly', label: 'currentOnly true/false', defaultValue: 'true' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/council/members',
      query: {
        electionId: values.electionId?.trim() || undefined,
        currentOnly: values.currentOnly?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'conferenceEventCreate',
    title: 'Conference Event Create',
    description: 'Create conference event with public sales date',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/events',
    fields: [
      { key: 'title', label: 'title', required: true },
      { key: 'startsAt', label: 'startsAt ISO datetime', required: true },
      { key: 'endsAt', label: 'endsAt ISO datetime' },
      { key: 'publicSalesAt', label: 'publicSalesAt ISO datetime', required: true },
      { key: 'status', label: 'status', defaultValue: 'published' },
      { key: 'capacity', label: 'capacity' },
      { key: 'location', label: 'location' },
      { key: 'description', label: 'description' },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/events',
      body: {
        title: values.title?.trim() || '',
        startsAt: values.startsAt?.trim() || '',
        endsAt: values.endsAt?.trim() || undefined,
        publicSalesAt: values.publicSalesAt?.trim() || '',
        status: values.status?.trim() || 'published',
        capacity: values.capacity?.trim() || undefined,
        location: values.location?.trim() || undefined,
        description: values.description?.trim() || undefined,
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceEventsList',
    title: 'Conference Events List',
    description: 'List conference events by status',
    category: 'Конференция',
    method: 'GET',
    pathTemplate: '/ops/voice/conference/events?status=:status&limit=:limit',
    fields: [
      { key: 'status', label: 'status', defaultValue: 'published' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/events',
      query: {
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'conferenceEventUpdate',
    title: 'Conference Event Update',
    description: 'Patch conference event status/dates/capacity',
    category: 'Конференция',
    method: 'PATCH',
    pathTemplate: '/ops/voice/conference/events/:id',
    fields: [
      { key: 'id', label: 'event id', required: true },
      { key: 'title', label: 'title' },
      { key: 'startsAt', label: 'startsAt ISO datetime' },
      { key: 'endsAt', label: 'endsAt ISO datetime' },
      { key: 'publicSalesAt', label: 'publicSalesAt ISO datetime' },
      { key: 'status', label: 'status' },
      { key: 'capacity', label: 'capacity' },
      { key: 'location', label: 'location' },
      { key: 'description', label: 'description' },
      { key: 'operatorNote', label: 'operatorNote' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/conference/events/${encodeURIComponent(values.id?.trim() || '')}`,
      body: {
        title: values.title?.trim() || undefined,
        startsAt: values.startsAt?.trim() || undefined,
        endsAt: values.endsAt?.trim() || undefined,
        publicSalesAt: values.publicSalesAt?.trim() || undefined,
        status: values.status?.trim() || undefined,
        capacity: values.capacity?.trim() || undefined,
        location: values.location?.trim() || undefined,
        description: values.description?.trim() || undefined,
        operatorNote: values.operatorNote?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceTicketBook',
    title: 'Conference Ticket Book',
    description: 'Book ticket via money or VP channel',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/tickets',
    fields: [
      { key: 'eventId', label: 'eventId', required: true },
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'ticketType', label: 'ticketType', defaultValue: 'standard', required: true },
      { key: 'channel', label: 'channel', defaultValue: 'money', required: true },
      { key: 'moneyAmount', label: 'moneyAmount' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'note', label: 'note' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/tickets',
      body: {
        eventId: values.eventId?.trim() || '',
        studentId: values.studentId?.trim() || '',
        ticketType: values.ticketType?.trim() || 'standard',
        channel: values.channel?.trim() || 'money',
        moneyAmount: values.moneyAmount?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
        note: values.note?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceTicketsList',
    title: 'Conference Tickets List',
    description: 'List conference tickets by filters',
    category: 'Конференция',
    method: 'GET',
    pathTemplate:
      '/ops/voice/conference/tickets?eventId=:eventId&studentId=:studentId&status=:status&ticketType=:ticketType&limit=:limit',
    fields: [
      { key: 'eventId', label: 'eventId' },
      { key: 'studentId', label: 'studentId' },
      { key: 'status', label: 'status' },
      { key: 'ticketType', label: 'ticketType' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/tickets',
      query: {
        eventId: values.eventId?.trim() || undefined,
        studentId: values.studentId?.trim() || undefined,
        status: values.status?.trim() || undefined,
        ticketType: values.ticketType?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'conferenceTicketConfirmPaid',
    title: 'Conference Ticket Confirm Paid',
    description: 'Mark money-channel ticket as paid',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/tickets/:id/confirm-paid',
    fields: [
      { key: 'id', label: 'ticket id', required: true },
      { key: 'provider', label: 'provider', defaultValue: 'tiptoppay' },
      { key: 'providerPaymentId', label: 'providerPaymentId' },
      { key: 'amount', label: 'amount' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'note', label: 'note' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/conference/tickets/${encodeURIComponent(values.id?.trim() || '')}/confirm-paid`,
      body: {
        provider: values.provider?.trim() || undefined,
        providerPaymentId: values.providerPaymentId?.trim() || undefined,
        amount: values.amount?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
        note: values.note?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceTicketCancel',
    title: 'Conference Ticket Cancel',
    description: 'Cancel ticket with optional VP refund',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/tickets/:id/cancel',
    fields: [
      { key: 'id', label: 'ticket id', required: true },
      { key: 'refundVp', label: 'refundVp true/false', defaultValue: 'true' },
      { key: 'reason', label: 'reason' },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/conference/tickets/${encodeURIComponent(values.id?.trim() || '')}/cancel`,
      body: {
        refundVp: values.refundVp?.trim().toLowerCase() !== 'false',
        reason: values.reason?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceReminderRun',
    title: 'Conference Reminder Sweep',
    description: 'Run 30/7/1-day conference reminders for subscribers',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/reminders/run',
    fields: [{ key: 'asOf', label: 'asOf ISO datetime' }],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/reminders/run',
      body: {
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceSpeakerApply',
    title: 'Conference Speaker Apply',
    description: 'Submit speaker-track application for event',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/speakers/applications',
    fields: [
      { key: 'eventId', label: 'eventId', required: true },
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'topic', label: 'topic', required: true },
      { key: 'summary', label: 'summary' },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/speakers/applications',
      body: {
        eventId: values.eventId?.trim() || '',
        studentId: values.studentId?.trim() || '',
        topic: values.topic?.trim() || '',
        summary: values.summary?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceSpeakerApplicationsList',
    title: 'Conference Speaker Applications',
    description: 'List speaker applications by event/student/status',
    category: 'Конференция',
    method: 'GET',
    pathTemplate:
      '/ops/voice/conference/speakers/applications?eventId=:eventId&studentId=:studentId&status=:status&limit=:limit',
    fields: [
      { key: 'eventId', label: 'eventId' },
      { key: 'studentId', label: 'studentId' },
      { key: 'status', label: 'status' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/conference/speakers/applications',
      query: {
        eventId: values.eventId?.trim() || undefined,
        studentId: values.studentId?.trim() || undefined,
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'conferenceSpeakerReview',
    title: 'Conference Speaker Review',
    description: 'Review speaker application (reviewing/approved/rejected)',
    category: 'Конференция',
    method: 'PATCH',
    pathTemplate: '/ops/voice/conference/speakers/applications/:id/review',
    fields: [
      { key: 'id', label: 'application id', required: true },
      { key: 'status', label: 'status', defaultValue: 'approved', required: true },
      { key: 'reviewedBy', label: 'reviewedBy' },
      { key: 'operatorNote', label: 'operatorNote' },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/conference/speakers/applications/${encodeURIComponent(values.id?.trim() || '')}/review`,
      body: {
        status: values.status?.trim() || '',
        reviewedBy: values.reviewedBy?.trim() || undefined,
        operatorNote: values.operatorNote?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceSpeakerInvoiceIssue',
    title: 'Conference Speaker Invoice',
    description: 'Issue speaker org-fee invoice (money channel)',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/speakers/applications/:id/invoice',
    fields: [
      { key: 'id', label: 'application id', required: true },
      { key: 'amount', label: 'amount', defaultValue: '30000', required: true },
      { key: 'provider', label: 'provider', defaultValue: 'tiptoppay' },
      { key: 'note', label: 'note' },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/conference/speakers/applications/${encodeURIComponent(values.id?.trim() || '')}/invoice`,
      body: {
        amount: values.amount?.trim() || '30000',
        provider: values.provider?.trim() || undefined,
        note: values.note?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'conferenceSpeakerInvoiceConfirmPaid',
    title: 'Conference Speaker Confirm Paid',
    description: 'Confirm payment for speaker org-fee invoice',
    category: 'Конференция',
    method: 'POST',
    pathTemplate: '/ops/voice/conference/speakers/applications/:id/confirm-paid',
    fields: [
      { key: 'id', label: 'application id', required: true },
      { key: 'providerPaymentId', label: 'providerPaymentId' },
      { key: 'provider', label: 'provider', defaultValue: 'tiptoppay' },
      { key: 'amount', label: 'amount' },
      { key: 'note', label: 'note' },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/conference/speakers/applications/${encodeURIComponent(values.id?.trim() || '')}/confirm-paid`,
      body: {
        providerPaymentId: values.providerPaymentId?.trim() || undefined,
        provider: values.provider?.trim() || undefined,
        amount: values.amount?.trim() || undefined,
        note: values.note?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'subscriptionsBillingRemindersRun',
    title: 'Subscriptions Billing Reminders',
    description: 'Run upcoming billing reminder sweep for active subscriptions',
    category: 'Оплаты',
    method: 'POST',
    pathTemplate: '/ops/voice/subscriptions/dunning/reminders/run',
    fields: [
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'horizonDays', label: 'horizonDays', defaultValue: '3' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/subscriptions/dunning/reminders/run',
      body: {
        asOf: values.asOf?.trim() || undefined,
        horizonDays: values.horizonDays?.trim() || '3',
      },
    }),
  },
  {
    id: 'subscriptionsDunningRun',
    title: 'Subscriptions Dunning Sweep',
    description: 'Run dunning with 7-day grace and downgrade logic',
    category: 'Оплаты',
    method: 'POST',
    pathTemplate: '/ops/voice/subscriptions/dunning/run',
    fields: [{ key: 'asOf', label: 'asOf ISO datetime' }],
    buildRequest: (values) => ({
      path: '/ops/voice/subscriptions/dunning/run',
      body: {
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'subscriptionsRestoreWindow',
    title: 'Subscriptions Restore Window',
    description: 'List canceled subscriptions eligible for 30-day restore',
    category: 'Оплаты',
    method: 'GET',
    pathTemplate:
      '/ops/voice/subscriptions/restore-window?studentId=:studentId&asOf=:asOf&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId' },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/subscriptions/restore-window',
      query: {
        studentId: values.studentId?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'subscriptionsRestore',
    title: 'Subscriptions Restore',
    description: 'Restore canceled subscription within 30-day window',
    category: 'Оплаты',
    method: 'POST',
    pathTemplate: '/ops/voice/subscriptions/:id/restore',
    fields: [
      { key: 'id', label: 'subscription id', required: true },
      { key: 'asOf', label: 'asOf ISO datetime' },
      { key: 'providerSubId', label: 'providerSubId' },
      { key: 'provider', label: 'provider' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/subscriptions/${encodeURIComponent(values.id?.trim() || '')}/restore`,
      body: {
        asOf: values.asOf?.trim() || undefined,
        providerSubId: values.providerSubId?.trim() || undefined,
        provider: values.provider?.trim() || undefined,
      },
    }),
  },
  {
    id: 'aiClubTrialStart',
    title: 'AI Club Trial Start',
    description: 'Start first free month after AI workshop',
    category: 'Оплаты',
    method: 'POST',
    pathTemplate: '/ops/voice/subscriptions/ai-club/trial/start',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'workshopRefId', label: 'workshopRefId' },
      { key: 'asOf', label: 'asOf ISO datetime' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/subscriptions/ai-club/trial/start',
      body: {
        studentId: values.studentId?.trim() || '',
        workshopRefId: values.workshopRefId?.trim() || undefined,
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'aiClubSubscriptionsList',
    title: 'AI Club Subscriptions List',
    description: 'List AI club subscriptions by status',
    category: 'Оплаты',
    method: 'GET',
    pathTemplate: '/ops/voice/subscriptions/ai-club/subscriptions?status=:status&limit=:limit',
    fields: [
      { key: 'status', label: 'status', defaultValue: 'trial' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/subscriptions/ai-club/subscriptions',
      query: {
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'aiClubTrialSweep',
    title: 'AI Club Trial Sweep',
    description: 'Move ended AI trials into first failed billing attempt',
    category: 'Оплаты',
    method: 'POST',
    pathTemplate: '/ops/voice/subscriptions/ai-club/trial/sweep',
    fields: [{ key: 'asOf', label: 'asOf ISO datetime' }],
    buildRequest: (values) => ({
      path: '/ops/voice/subscriptions/ai-club/trial/sweep',
      body: {
        asOf: values.asOf?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpShopItemsList',
    title: 'VP Shop Items List',
    description: 'List VP shop catalog items',
    category: 'VP Магазин',
    method: 'GET',
    pathTemplate: '/ops/voice/shop/items?active=:active&category=:category',
    fields: [
      { key: 'active', label: 'active true/false' },
      { key: 'category', label: 'category' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/shop/items',
      query: {
        active: values.active?.trim() || undefined,
        category: values.category?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpShopItemCreate',
    title: 'VP Shop Create Item',
    description: 'Create VP shop catalog item',
    category: 'VP Магазин',
    method: 'POST',
    pathTemplate: '/ops/voice/shop/items',
    fields: [
      { key: 'name', label: 'name', required: true },
      { key: 'priceVp', label: 'priceVp', defaultValue: '30', required: true },
      { key: 'stock', label: 'stock (blank = unlimited)' },
      { key: 'category', label: 'category' },
      { key: 'description', label: 'description' },
      { key: 'imageUrl', label: 'imageUrl' },
      { key: 'active', label: 'active true/false', defaultValue: 'true' },
      { key: 'minLevelKey', label: 'minLevelKey' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/shop/items',
      body: {
        name: values.name?.trim() || '',
        priceVp: values.priceVp?.trim() || '30',
        stock: values.stock?.trim() || undefined,
        category: values.category?.trim() || undefined,
        description: values.description?.trim() || undefined,
        imageUrl: values.imageUrl?.trim() || undefined,
        active: values.active?.trim().toLowerCase() !== 'false',
        minLevelKey: values.minLevelKey?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpShopItemUpdate',
    title: 'VP Shop Update Item',
    description: 'Update or deactivate VP shop item',
    category: 'VP Магазин',
    method: 'PATCH',
    pathTemplate: '/ops/voice/shop/items/:id',
    fields: [
      { key: 'id', label: 'item id', required: true },
      { key: 'name', label: 'name' },
      { key: 'priceVp', label: 'priceVp' },
      { key: 'stock', label: 'stock (empty keeps current)' },
      { key: 'category', label: 'category' },
      { key: 'description', label: 'description' },
      { key: 'imageUrl', label: 'imageUrl' },
      { key: 'active', label: 'active true/false' },
      { key: 'minLevelKey', label: 'minLevelKey' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/shop/items/${encodeURIComponent(values.id?.trim() || '')}`,
      body: {
        name: values.name?.trim() || undefined,
        priceVp: values.priceVp?.trim() || undefined,
        stock: values.stock?.trim() || undefined,
        category: values.category?.trim() || undefined,
        description: values.description?.trim() || undefined,
        imageUrl: values.imageUrl?.trim() || undefined,
        active: values.active?.trim()
          ? values.active.trim().toLowerCase() !== 'false'
          : undefined,
        minLevelKey: values.minLevelKey?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpShopPlaceOrder',
    title: 'VP Shop Place Order',
    description: 'Place VP shop order for student',
    category: 'VP Магазин',
    method: 'POST',
    pathTemplate: '/ops/voice/shop/orders',
    fields: [
      { key: 'studentId', label: 'studentId', required: true },
      { key: 'itemId', label: 'itemId', required: true },
      { key: 'note', label: 'note' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/shop/orders',
      body: {
        studentId: values.studentId?.trim() || '',
        itemId: values.itemId?.trim() || '',
        note: values.note?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpShopOrdersList',
    title: 'VP Shop Orders List',
    description: 'List VP orders with optional filters',
    category: 'VP Магазин',
    method: 'GET',
    pathTemplate: '/ops/voice/shop/orders?studentId=:studentId&status=:status&limit=:limit',
    fields: [
      { key: 'studentId', label: 'studentId' },
      { key: 'status', label: 'status' },
      { key: 'limit', label: 'limit', defaultValue: '50' },
    ],
    buildRequest: (values) => ({
      path: '/ops/voice/shop/orders',
      query: {
        studentId: values.studentId?.trim() || undefined,
        status: values.status?.trim() || undefined,
        limit: values.limit?.trim() || '50',
      },
    }),
  },
  {
    id: 'vpShopOrderFulfill',
    title: 'VP Shop Fulfill Order',
    description: 'Mark VP order as fulfilled',
    category: 'VP Магазин',
    method: 'POST',
    pathTemplate: '/ops/voice/shop/orders/:id/fulfill',
    fields: [
      { key: 'id', label: 'order id', required: true },
      { key: 'note', label: 'note' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/shop/orders/${encodeURIComponent(values.id?.trim() || '')}/fulfill`,
      body: {
        note: values.note?.trim() || undefined,
      },
    }),
  },
  {
    id: 'vpShopOrderCancel',
    title: 'VP Shop Cancel Order',
    description: 'Cancel VP order with optional refund',
    category: 'VP Магазин',
    method: 'POST',
    pathTemplate: '/ops/voice/shop/orders/:id/cancel',
    fields: [
      { key: 'id', label: 'order id', required: true },
      { key: 'reason', label: 'reason' },
      { key: 'refundVp', label: 'refundVp true/false', defaultValue: 'true' },
    ],
    buildRequest: (values) => ({
      path: `/ops/voice/shop/orders/${encodeURIComponent(values.id?.trim() || '')}/cancel`,
      body: {
        reason: values.reason?.trim() || undefined,
        refundVp: values.refundVp?.trim().toLowerCase() !== 'false',
      },
    }),
  },
  {
    id: 'attendanceImport',
    title: 'Attendance Import',
    description: 'Import attendance from Google Sheets sync endpoint',
    category: 'Sync/Import',
    method: 'POST',
    pathTemplate: '/sync/sheets/attendance/import?dryRun=:dryRun',
    fields: [
      {
        key: 'dryRun',
        label: 'dryRun',
        defaultValue: 'true',
      },
    ],
    buildRequest: (values) => ({
      path: '/sync/sheets/attendance/import',
      query: {
        dryRun: values.dryRun?.trim() || 'true',
      },
    }),
  },
]

export const endpointDefinitionMap = endpointDefinitions.reduce<Record<string, EndpointDefinition>>(
  (accumulator, definition) => {
    accumulator[definition.id] = definition
    return accumulator
  },
  {},
)

export const endpointInitialInputs = endpointDefinitions.reduce<
  Record<string, Record<string, string>>
>((accumulator, endpoint) => {
  accumulator[endpoint.id] = endpoint.fields.reduce<Record<string, string>>((fieldAccumulator, field) => {
    fieldAccumulator[field.key] = field.defaultValue || ''
    return fieldAccumulator
  }, {})
  return accumulator
}, {})
